import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Plus,
  Search,
} from "lucide-react";
import { medicineQueryKeys, medicinesApi } from "../../shared/api/medicines";
import type {
  MedicineClassification,
  MedicineListFilter,
  MedicineResponse,
  MedicineSummaryResponse,
} from "../../shared/types/medicine";
import {
  formatDate,
  formatVnd,
  medicineClassificationLabel,
} from "../../shared/format";
import { apiError } from "../../shared/api/client";
import { MedicineFormModal } from "./components/MedicineFormModal";
import { DrugDbSearchModal } from "./components/DrugDbSearchModal";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 20;

const SELECT_CLASS =
  "h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function MedicinesSettingsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { medicineId } = useParams<{ medicineId: string }>();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [classification, setClassification] = useState<MedicineClassification | "">("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [visibleOnly, setVisibleOnly] = useState(false);
  const [page, setPage] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MedicineResponse | null>(null);
  const [drugDbOpen, setDrugDbOpen] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const sel = useRowSelection();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const filter: MedicineListFilter = {
    q: debouncedQ || null,
    classification: classification || null,
    categoryId: categoryId || null,
    visibleOnly: visibleOnly ? true : null,
  };

  const list = useQuery({
    queryKey: medicineQueryKeys.list(filter, page),
    queryFn: () => medicinesApi.list(filter, { page, size: PAGE_SIZE, sort: "name" }),
    placeholderData: (prev) => prev,
  });

  const categories = useQuery({
    queryKey: medicineQueryKeys.categories(),
    queryFn: () => medicinesApi.listCategories(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => medicinesApi.softDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: medicineQueryKeys.all }),
  });

  const toggleVisible = useMutation({
    mutationFn: ({ id, visible }: { id: string; visible: boolean }) =>
      visible ? medicinesApi.hide(id) : medicinesApi.show(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: medicineQueryKeys.all }),
  });

  const rows = list.data?.content ?? [];
  const allSel = rows.length > 0 && rows.every((m) => sel.selectedIds.has(m.id));
  const someSel = rows.some((m) => sel.selectedIds.has(m.id)) && !allSel;
  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], medicinesApi.remove);
    setBulkPending(false);
    sel.clear();
    qc.invalidateQueries({ queryKey: medicineQueryKeys.all });
    setBulkError(
      failed.length ? `${failed.length} thuốc không xoá được (đang được dùng).` : null,
    );
  }

  function onEdit(id: string) {
    medicinesApi.get(id).then((m) => {
      setEditing(m);
      setFormOpen(true);
    });
  }

  function onSaved() {
    setFormOpen(false);
    setEditing(null);
    qc.invalidateQueries({ queryKey: medicineQueryKeys.all });
    if (medicineId) navigate("/settings/medicines", { replace: true });
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
    if (medicineId) navigate("/settings/medicines", { replace: true });
  }

  // Deep-linking: /settings/medicines/new opens the create modal, and
  // /settings/medicines/:id opens that medicine in the edit modal — so the page
  // (and its "add medicine" flow) is reachable by URL instead of 404-ing.
  useEffect(() => {
    if (!medicineId) return;
    if (medicineId === "new") {
      setEditing(null);
      setFormOpen(true);
      return;
    }
    let alive = true;
    medicinesApi
      .get(medicineId)
      .then((m) => {
        if (!alive) return;
        setEditing(m);
        setFormOpen(true);
      })
      .catch(() => {
        if (alive) navigate("/settings/medicines", { replace: true });
      });
    return () => {
      alive = false;
    };
  }, [medicineId, navigate]);

  const totalPages = list.data?.totalPages ?? 0;
  const totalElements = list.data?.totalElements ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Danh mục thuốc & vật tư"
        description="Quản lý catalog thuốc của phòng khám. Bạn có thể tạo thủ công hoặc import nhanh từ Cổng Dược Quốc Gia (donthuocquocgia.vn) với mã DQG chuẩn."
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setDrugDbOpen(true)}>
              <Search />
              Tra Cổng Dược Quốc Gia
            </Button>
            <Button
              type="button"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Plus />
              Thêm thuốc
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo mã, tên, hoạt chất, hãng dược…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={SELECT_CLASS}
            value={classification}
            onChange={(e) => {
              setClassification(e.target.value as MedicineClassification | "");
              setPage(0);
            }}
            aria-label="Lọc theo phân loại"
          >
            <option value="">Tất cả phân loại</option>
            {(Object.keys(medicineClassificationLabel) as MedicineClassification[]).map((c) => (
              <option key={c} value={c}>{medicineClassificationLabel[c]}</option>
            ))}
          </select>
          <select
            className={SELECT_CLASS}
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(0);
            }}
            aria-label="Lọc theo nhóm"
          >
            <option value="">Tất cả nhóm</option>
            {categories.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-foreground">
            <input
              type="checkbox"
              className="size-4 rounded border-input accent-primary"
              checked={visibleOnly}
              onChange={(e) => {
                setVisibleOnly(e.target.checked);
                setPage(0);
              }}
            />
            Chỉ hiển thị
          </label>
        </div>
      </div>

      {bulkError && <p className="text-sm text-destructive">{bulkError}</p>}
      {sel.count > 0 && (
        <BulkDeleteBar
          count={sel.count}
          noun="thuốc"
          pending={bulkPending}
          onClear={sel.clear}
          onDelete={bulkDelete}
        />
      )}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-9 text-center">
                <input
                  type="checkbox"
                  className="size-4 rounded border-input accent-primary"
                  aria-label="Chọn tất cả"
                  checked={allSel}
                  ref={(el) => {
                    if (el) el.indeterminate = someSel;
                  }}
                  onChange={() => {
                    const next = new Set(sel.selectedIds);
                    if (allSel) rows.forEach((m) => next.delete(m.id));
                    else rows.forEach((m) => next.add(m.id));
                    sel.setSelectedIds(next);
                  }}
                />
              </TableHead>
              <TableHead>Mã thuốc</TableHead>
              <TableHead>Tên thuốc</TableHead>
              <TableHead>Hoạt chất</TableHead>
              <TableHead>Hàm lượng</TableHead>
              <TableHead>Đơn vị</TableHead>
              <TableHead>Mã DQG</TableHead>
              <TableHead>Barcode</TableHead>
              <TableHead className="text-right">Giá bán</TableHead>
              <TableHead>Phân loại</TableHead>
              <TableHead>Nhóm</TableHead>
              <TableHead>Cty dược</TableHead>
              <TableHead>Hiển thị</TableHead>
              <TableHead>Cập nhật</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isLoading && (
              <TableRow>
                <TableCell colSpan={15} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {list.isError && (
              <TableRow>
                <TableCell colSpan={15} className="py-8 text-center text-destructive">
                  ⚠ {apiError(list.error).detail}
                </TableCell>
              </TableRow>
            )}
            {!list.isLoading && (list.data?.content.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={15} className="py-8 text-center text-muted-foreground">
                  Chưa có thuốc nào. Bấm "Thêm thuốc" hoặc "Tra Cổng Dược Quốc Gia" để bắt đầu.
                </TableCell>
              </TableRow>
            )}
            {list.data?.content.map((m) => (
              <MedicineRow
                key={m.id}
                m={m}
                selected={sel.selectedIds.has(m.id)}
                onToggleSelect={() => sel.toggle(m.id)}
                onEdit={() => onEdit(m.id)}
                onToggleVisible={() =>
                  toggleVisible.mutate({ id: m.id, visible: m.visible })
                }
                onDelete={() => {
                  if (confirm(`Xoá ${m.name}? Có thể khôi phục sau.`)) remove.mutate(m.id);
                }}
                busy={toggleVisible.isPending || remove.isPending}
              />
            ))}
          </TableBody>
        </Table>
        {totalPages > 0 && (
          <div className="flex flex-col gap-2 border-t px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="text-muted-foreground">
              Hiển thị {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, totalElements)} trong {totalElements}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page === 0}
                onClick={() => setPage(0)}
                aria-label="Trang đầu"
              >
                <ChevronsLeft />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                aria-label="Trang trước"
              >
                <ChevronLeft />
              </Button>
              <span className="px-2 tabular-nums text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
                aria-label="Trang sau"
              >
                <ChevronRight />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(totalPages - 1)}
                aria-label="Trang cuối"
              >
                <ChevronsRight />
              </Button>
            </div>
          </div>
        )}
      </div>

      <MedicineFormModal
        open={formOpen}
        mode={editing ? "edit" : "create"}
        initial={editing}
        onClose={closeForm}
        onSaved={onSaved}
      />
      <DrugDbSearchModal
        open={drugDbOpen}
        onClose={() => setDrugDbOpen(false)}
        onImported={() => {
          qc.invalidateQueries({ queryKey: medicineQueryKeys.all });
        }}
      />
    </div>
  );
}

function MedicineRow({
  m,
  selected,
  onToggleSelect,
  onEdit,
  onToggleVisible,
  onDelete,
  busy,
}: {
  m: MedicineSummaryResponse;
  selected: boolean;
  onToggleSelect: () => void;
  onEdit: () => void;
  onToggleVisible: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="text-center">
        <input
          type="checkbox"
          className="size-4 rounded border-input accent-primary"
          aria-label="Chọn dòng"
          checked={selected}
          onChange={onToggleSelect}
        />
      </TableCell>
      <TableCell className="font-mono text-xs">{m.internalCode}</TableCell>
      <TableCell>
        <button
          type="button"
          onClick={onEdit}
          title="Sửa thuốc"
          className="text-left font-bold text-primary hover:underline"
        >
          {m.name}
        </button>
        {!m.active && (
          <Badge variant="muted" className="ml-1.5">Đã tắt</Badge>
        )}
      </TableCell>
      <TableCell>{m.activeIngredient ?? "—"}</TableCell>
      <TableCell>{m.strength ?? "—"}</TableCell>
      <TableCell>{m.unit}</TableCell>
      <TableCell className="font-mono text-xs">{m.nationalDrugCode ?? "—"}</TableCell>
      <TableCell className="font-mono text-xs">{m.barcode ?? "—"}</TableCell>
      <TableCell className="text-right">{formatVnd(m.sellPriceVnd)}</TableCell>
      <TableCell>{medicineClassificationLabel[m.classification]}</TableCell>
      <TableCell>{m.categoryName ?? "—"}</TableCell>
      <TableCell>{m.manufacturer ?? "—"}</TableCell>
      <TableCell>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={onToggleVisible}
          disabled={busy}
          title={m.visible ? "Ẩn khỏi dropdown kê đơn" : "Hiển thị trong dropdown kê đơn"}
        >
          {m.visible ? "✓ Hiển thị" : "Ẩn"}
        </Button>
      </TableCell>
      <TableCell className="font-mono text-xs">{formatDate(m.updatedAt)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={onEdit}
          >
            Sửa
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={onDelete}
            disabled={busy}
          >
            Xoá
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
